/**
 * Mock Data Generators
 * Generate realistic test data for payments, rates, and provider status
 */

import { 
  MockPayment, 
  MockSettlement, 
  MockProviderStatus, 
  MockExchangeRate, 
  MockDashboardStats 
} from './mockApi'

// Sample business names for realistic data
const businessNames = [
  'Hong Kong Trading Co.',
  'Asia Pacific Imports',
  'Golden Dragon Electronics',
  'Pearl River Textiles',
  'Jade Mountain Logistics',
  'Phoenix Tech Solutions',
  'Dragon Bay Manufacturing',
  'Lotus Flower Exports'
]

// Sample invoice numbers
const generateInvoiceNumber = (): string => {
  const prefix = 'INV'
  const year = new Date().getFullYear()
  const number = Math.floor(Math.random() * 9999).toString().padStart(4, '0')
  return `${prefix}-${year}-${number}`
}

// Generate transaction hash
const generateTransactionHash = (): string => {
  return '0x' + Array.from({ length: 64 }, () => 
    Math.floor(Math.random() * 16).toString(16)
  ).join('')
}

// Generate FPS reference
const generateFpsReference = (): string => {
  return 'FPS' + Math.floor(Math.random() * 1000000000).toString().padStart(9, '0')
}

/**
 * Generate mock payment data
 */
export const generateMockPayment = (overrides: Partial<MockPayment> = {}): MockPayment => {
  const stablecoins: MockPayment['stablecoinType'][] = ['USDT', 'USDC', 'HKD₮', 'CNH₮']
  const providers: MockPayment['provider'][] = ['HashKey', 'OSL', 'Moonpay']
  const rails: MockPayment['settlementRail'][] = ['FPS', 'CIPS', 'e-CNY']
  const statuses: MockPayment['status'][] = ['pending', 'confirmed', 'failed', 'expired']
  
  const stablecoin = stablecoins[Math.floor(Math.random() * stablecoins.length)]
  const provider = providers[Math.floor(Math.random() * providers.length)]
  const rail = rails[Math.floor(Math.random() * rails.length)]
  const status = statuses[Math.floor(Math.random() * statuses.length)]
  
  const amount = Math.floor(Math.random() * 50000) + 1000 // HKD 1,000 - 50,000
  const exchangeRate = getExchangeRateForStablecoin(stablecoin)
  const stablecoinAmount = amount / exchangeRate
  
  const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
  const confirmedAt = status === 'confirmed' ? 
    new Date(new Date(createdAt).getTime() + Math.random() * 60 * 60 * 1000).toISOString() : 
    undefined

  return {
    id: `pay_${Math.random().toString(36).substr(2, 9)}`,
    invoiceId: `inv_${Math.random().toString(36).substr(2, 9)}`,
    invoiceNumber: generateInvoiceNumber(),
    amount,
    stablecoinAmount: Math.round(stablecoinAmount * 1000000) / 1000000, // 6 decimal places
    stablecoinType: stablecoin,
    provider,
    exchangeRate,
    transactionHash: status !== 'pending' ? generateTransactionHash() : undefined,
    blockNumber: status !== 'pending' ? Math.floor(Math.random() * 1000000) + 18000000 : undefined,
    confirmations: status === 'confirmed' ? Math.floor(Math.random() * 50) + 12 : 0,
    status,
    settlementRail: rail,
    expiresAt: status === 'pending' ? 
      new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : 
      undefined,
    createdAt,
    confirmedAt,
    ...overrides
  }
}

/**
 * Generate mock settlement data
 */
export const generateMockSettlement = (overrides: Partial<MockSettlement> = {}): MockSettlement => {
  const rails: MockSettlement['settlementRail'][] = ['FPS', 'CIPS', 'e-CNY']
  const statuses: MockSettlement['status'][] = ['pending', 'processing', 'completed', 'failed']
  
  const rail = rails[Math.floor(Math.random() * rails.length)]
  const status = statuses[Math.floor(Math.random() * statuses.length)]
  
  const transactionCount = Math.floor(Math.random() * 20) + 1
  const totalAmount = Math.floor(Math.random() * 500000) + 10000 // HKD 10,000 - 500,000
  const processingFee = Math.round(totalAmount * 0.002 * 100) / 100 // 0.2% processing fee
  const fpsFee = rail === 'FPS' ? Math.round(totalAmount * 0.001 * 100) / 100 : 0 // 0.1% FPS fee
  const netAmount = totalAmount - processingFee - fpsFee
  
  const estimatedTimes = {
    'FPS': '15 minutes',
    'CIPS': 'Same day',
    'e-CNY': 'Instant'
  }
  
  const createdAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
  const completedAt = status === 'completed' ? 
    new Date(new Date(createdAt).getTime() + Math.random() * 24 * 60 * 60 * 1000).toISOString() : 
    undefined

  return {
    id: `set_${Math.random().toString(36).substr(2, 9)}`,
    batchId: `batch_${Math.random().toString(36).substr(2, 9)}`,
    totalAmount,
    transactionCount,
    processingFee,
    fpsFee,
    netAmount,
    providerBreakdown: {
      HashKey: Math.round(totalAmount * 0.4),
      OSL: Math.round(totalAmount * 0.35),
      Moonpay: Math.round(totalAmount * 0.25)
    },
    status,
    fpsReference: rail === 'FPS' ? generateFpsReference() : undefined,
    settlementRail: rail,
    estimatedTime: estimatedTimes[rail],
    createdAt,
    completedAt,
    ...overrides
  }
}

/**
 * Generate mock provider status
 */
export const generateMockProviderStatus = (provider: MockProviderStatus['provider']): MockProviderStatus => {
  const statuses: MockProviderStatus['status'][] = ['healthy', 'degraded', 'down']
  const status = statuses[Math.floor(Math.random() * statuses.length)]
  
  // Adjust response time based on status
  let responseTime: number
  let uptime: number
  
  switch (status) {
    case 'healthy':
      responseTime = Math.floor(Math.random() * 200) + 100 // 100-300ms
      uptime = 99.5 + Math.random() * 0.5 // 99.5-100%
      break
    case 'degraded':
      responseTime = Math.floor(Math.random() * 800) + 500 // 500-1300ms
      uptime = 95 + Math.random() * 4 // 95-99%
      break
    case 'down':
      responseTime = 0
      uptime = Math.random() * 90 // 0-90%
      break
  }
  
  const supportedStablecoins = {
    'HashKey': ['USDT', 'USDC', 'HKD₮', 'CNH₮'],
    'OSL': ['USDT', 'USDC', 'HKD₮'],
    'Moonpay': ['USDT', 'USDC']
  }
  
  const supportedRails = {
    'HashKey': ['FPS', 'CIPS', 'e-CNY'],
    'OSL': ['FPS', 'CIPS'],
    'Moonpay': ['FPS']
  }

  return {
    provider,
    status,
    responseTime: Math.round(responseTime),
    uptime: Math.round(uptime * 100) / 100,
    lastUpdate: new Date().toISOString(),
    supportedStablecoins: supportedStablecoins[provider],
    supportedRails: supportedRails[provider]
  }
}

/**
 * Generate enhanced provider status with full Provider interface
 */
export const generateMockProvider = (providerName: 'HashKey' | 'OSL' | 'Moonpay'): any => {
  const baseProvider = generateMockProviderStatus(providerName)
  
  const regions = {
    'HashKey': 'Hong Kong',
    'OSL': 'Hong Kong', 
    'Moonpay': 'International'
  }
  
  const isRegulated = {
    'HashKey': true,
    'OSL': true,
    'Moonpay': false
  }

  return {
    id: providerName.toLowerCase(),
    name: providerName,
    status: baseProvider.status,
    responseTime: baseProvider.responseTime,
    uptime: baseProvider.uptime,
    lastUpdate: baseProvider.lastUpdate,
    supportedStablecoins: baseProvider.supportedStablecoins,
    supportedRails: baseProvider.supportedRails,
    region: regions[providerName],
    isRegulated: isRegulated[providerName]
  }
}

/**
 * Generate mock exchange rates
 */
export const generateMockExchangeRate = (stablecoin: MockExchangeRate['stablecoin']): MockExchangeRate => {
  const baseRates = {
    'USDT': { HKD: 7.8, CNY: 7.2, USD: 1.0 },
    'USDC': { HKD: 7.8, CNY: 7.2, USD: 1.0 },
    'HKD₮': { HKD: 1.0, CNY: 0.92, USD: 0.128 },
    'CNH₮': { HKD: 1.09, CNY: 1.0, USD: 0.139 }
  }
  
  const spreads = {
    'USDT': 0.003, // 0.3%
    'USDC': 0.003, // 0.3%
    'HKD₮': 0.002, // 0.2%
    'CNH₮': 0.004  // 0.4%
  }
  
  // Simulate slight rate fluctuations
  const fluctuation = (Math.random() - 0.5) * 0.01 // ±0.5%
  const currency = 'HKD' as const // Default to HKD for demo
  const baseRate = baseRates[stablecoin][currency]
  const rate = baseRate * (1 + fluctuation)
  
  return {
    stablecoin,
    currency,
    rate: Math.round(rate * 1000000) / 1000000, // 6 decimal places
    spread: spreads[stablecoin],
    lastUpdated: new Date().toISOString()
  }
}

/**
 * Generate mock dashboard stats
 */
export const generateMockDashboardStats = (): MockDashboardStats => {
  const totalRevenue = Math.floor(Math.random() * 2000000) + 500000 // HKD 500K - 2.5M
  const totalInvoices = Math.floor(Math.random() * 1000) + 200
  const totalPayments = Math.floor(totalInvoices * 0.8) // 80% payment rate
  const pendingSettlements = Math.floor(Math.random() * 10) + 1
  
  return {
    totalRevenue,
    totalInvoices,
    totalPayments,
    pendingSettlements,
    revenueChange: (Math.random() - 0.3) * 50, // -15% to +35% change
    invoicesChange: (Math.random() - 0.2) * 40, // -8% to +32% change
    paymentsChange: (Math.random() - 0.2) * 40, // -8% to +32% change
    settlementsChange: (Math.random() - 0.4) * 60, // -24% to +36% change
    savingsVsTraditional: Math.floor(Math.random() * 50000) + 10000, // HKD 10K - 60K savings
    averageSettlementTime: Math.floor(Math.random() * 30) + 15 // 15-45 minutes
  }
}

/**
 * Generate multiple mock payments
 */
export const generateMockPayments = (count: number): MockPayment[] => {
  return Array.from({ length: count }, () => generateMockPayment())
}

/**
 * Generate multiple mock settlements
 */
export const generateMockSettlements = (count: number): MockSettlement[] => {
  return Array.from({ length: count }, () => generateMockSettlement())
}

/**
 * Generate all provider statuses
 */
export const generateAllProviderStatuses = (): MockProviderStatus[] => {
  const providers: MockProviderStatus['provider'][] = ['HashKey', 'OSL', 'Moonpay']
  return providers.map(provider => generateMockProviderStatus(provider))
}

/**
 * Generate all providers with full interface
 */
export const generateAllProviders = (): any[] => {
  const providers: ('HashKey' | 'OSL' | 'Moonpay')[] = ['HashKey', 'OSL', 'Moonpay']
  return providers.map(provider => generateMockProvider(provider))
}

/**
 * Generate all exchange rates
 */
export const generateAllExchangeRates = (): MockExchangeRate[] => {
  const stablecoins: MockExchangeRate['stablecoin'][] = ['USDT', 'USDC', 'HKD₮', 'CNH₮']
  return stablecoins.map(stablecoin => generateMockExchangeRate(stablecoin))
}

/**
 * Helper function to get exchange rate for a stablecoin
 */
export const getExchangeRateForStablecoin = (stablecoin: MockExchangeRate['stablecoin']): number => {
  const rates = generateMockExchangeRate(stablecoin)
  return rates.rate
}

/**
 * Generate chart data for dashboard
 */
export const generateMockChartData = (days: number = 7): Array<{ date: string; revenue: number; payments: number }> => {
  const data = []
  const now = new Date()
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
    const revenue = Math.floor(Math.random() * 50000) + 10000
    const payments = Math.floor(Math.random() * 20) + 5
    
    data.push({
      date: date.toISOString().split('T')[0],
      revenue,
      payments
    })
  }
  
  return data
}